import React from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { size } from 'lodash-es'
import styled from 'styled-components/native'

import { getFriendlyDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  Capacity,
  ProviderExpeditedBookingConfig,
  ProviderUser,
} from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { ExpeditedBookingIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExpeditedBookingIcon'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'

import styles from './availabilityCard.module.scss'
import { getAuthExpeditedBookingConfig } from '../../../data/auth/authSelectors'
import { getProgramIcon } from '../../constants/programIconMap'

const ProgramCapacityContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
})

const ProgramIconWrapper = styled.View({
  borderRadius: '5px',
  height: '44px',
  overflow: 'hidden',
  width: '44px',
})

const CapacitiesMinified: React.FC<CapacitiesMinifiedProps> = ({
  provider,
  currentAvailabilityStatusText,
  handleUpdateClick,
  currentCapacity,
  hideProgramIcon,
}) => {
  const intl = useIntl()
  const providerExpeditedBookingConfig: ProviderExpeditedBookingConfig | undefined =
    useSelector(getAuthExpeditedBookingConfig)
  return (
    <>
      {Object.entries(provider.capacity_value).map(([programName]) => {
        const ProgramIcon = getProgramIcon(programName)
        // NOTE: careTypeLabel is a user-facing display string, not a stable identifier.
        // Using it in data-test-id means test selectors will break if labels are translated or renamed.
        const careTypeLabel = getProgramDisplayLabel(intl, programName)
        return (
          <div className={styles['capacity-card']} key={programName}>
            {size(provider.capacity_value) > 1 ? (
              <div
                className={styles['icon-container']}
                data-test-id={`CapacitiesMinified-${careTypeLabel}-${currentCapacity[programName as keyof Capacity]}`}
              >
                {!hideProgramIcon && ProgramIcon && (
                  <span className={styles['icon-inner-container']}>
                    <ProgramIcon
                      width={44}
                      tooltip={`${careTypeLabel} - ${currentAvailabilityStatusText(
                        programName,
                      )} Last updated ${getFriendlyDate({
                        date: provider.capacity_updated_at,
                        intl,
                        displayWeekdayForRecentDates: false,
                      })}`}
                    />
                    {providerExpeditedBookingConfig && providerExpeditedBookingConfig[programName]?.enabled && (
                      <div className={styles['expedited-booking-icon-wrapper']}>
                        <ExpeditedBookingIcon width={24} />
                      </div>
                    )}
                  </span>
                )}
                <span className={styles['icon-label']}>{currentCapacity[programName as keyof Capacity]}</span>
              </div>
            ) : (
              <ProgramCapacityContainer>
                <div
                  className={styles['icon-container']}
                  data-test-id={`CapacitiesMinified-${careTypeLabel}-${currentCapacity[programName as keyof Capacity]}`}
                >
                  {!hideProgramIcon && ProgramIcon && (
                    <span className={styles['icon-inner-container']}>
                      <ProgramIconWrapper>
                        <ProgramIcon
                          width={44}
                          tooltip={`${careTypeLabel} - ${currentAvailabilityStatusText(
                            programName,
                          )} Last updated ${getFriendlyDate({
                            date: provider.capacity_updated_at,
                            intl,
                            displayWeekdayForRecentDates: false,
                          })}`}
                        />
                      </ProgramIconWrapper>
                      {providerExpeditedBookingConfig && providerExpeditedBookingConfig[programName]?.enabled && (
                        <div className={styles['expedited-booking-icon-wrapper']}>
                          <ExpeditedBookingIcon width={24} />
                        </div>
                      )}
                    </span>
                  )}
                </div>
                <div className={styles['session-info']}>
                  {
                    <p data-test-id='CapacitiesMinified-program-capacity' className={styles['capacity-title']}>
                      {size(provider.capacity_value) > 1 && `${careTypeLabel} -`}{' '}
                      {currentAvailabilityStatusText(programName)}
                    </p>
                  }
                  {provider.capacity_updated_at && (
                    <p data-test-id='CapacitiesMinified-updated-date' className={styles['update-date']}>
                      Last updated{' '}
                      {getFriendlyDate({
                        date: provider.capacity_updated_at,
                        intl,
                        displayWeekdayForRecentDates: false,
                      })}
                    </p>
                  )}
                </div>
              </ProgramCapacityContainer>
            )}
          </div>
        )
      })}
      <PrimaryButton testID={'CapacitiesMinified-update'} onPress={handleUpdateClick} text={'Update'} />
    </>
  )
}

type CapacitiesMinifiedProps = {
  provider: ProviderUser
  currentAvailabilityStatusText: (program_name: string) => React.ReactNode
  handleUpdateClick: () => void
  currentCapacity: Capacity
  hideProgramIcon?: boolean
}

export default CapacitiesMinified
